import React, { RefObject } from "react";
import {
  FetchedCountryProductYearData,
  FetchedDatum,
  FetchedProductYearData,
} from "./graphQLTypes";
import {
  axisLabelsSize,
  marginalLegendSize,
  Root,
  topLabelSize,
  xAxisTicksHeight,
  yAxisTicksWidth,
} from "./Grid";
import transform from "./transform";

import { FluentBundle } from "fluent";
import { GetString } from "fluent-react";
import {
  CountryYear,
  LookBackYearRange,
  Product,
  ProductLevel,
} from "../../graphQL/types/shared";
import { gql, useQuery } from "../../graphQL/useQuery";
import { SlideElementProps } from "../../presentationManager/PresentationManager";
import ErrorOverlay from "../../sharedComponents/GraphError";
import Loading from "../../sharedComponents/GraphLoading";
import useLastSuccesfulFetch from "../../tree/newChart/useLastSuccesfulFetch";
import useDOMElemSizeMonitoring, {
  Size as MeasuredDOMElementSize,
} from "../../tree/useDOMElemSizeMonitoringHook";
import usePromotableZIndex from "../usePromotableZIndex";
import { ExportsChangeNodeSizing } from "../Utils";
import Chart from "./Chart";

const hoverTransitionDuration = 200;
const dataSetTransitionDuration = 1250;

const zIndices = {
  leftLegendContainer: 500,
  bottomLegendContainer: 510,
  yAxisLabelContainer: 530,
  chartContainer: 550,
  yTicksContainer: 560,
  xTicksOuterContainer: 560,
  spinner: 570,
  topLabelContainer: 570,
  xAxisLabelContainer: 580,
  tooltipsContainer: 590,
};

const promotedZIndex = 3000;

const QUERY = gql`
  query ExportGrowthDynamicsChart(
    $location: ID!
    $year: Int!
    $yearRange: LookBackYearRange!
    $productLevel: ProductLevel!
  ) {
    countryProductLookback(
      location: $location
      yearRange: $yearRange
      productLevel: $productLevel
    ) {
      product {
        id
        shortName
        longName
        code
        topLevelParent {
          id
        }
      }
      exportGrowthCAGR: exportValueConstCagr
    }

    allCountryProductYear(
      location: $location
      year: $year
      productClass: HS
      productLevel: $productLevel
    ) {
      product {
        id
      }
      exportValue
    }

    allProductYear(productClass: HS, productLevel: $productLevel, year: $year) {
      product {
        id
      }
      globalExportValue
      pci
    }

    countryYear(location: $location, year: $year) {
      eci
    }

    countryLookback(id: $location, yearRange: $yearRange) {
      largestContributingExportProduct {
        id
      }
    }
  }
`;

interface SuccessResponse {
  countryProductLookback: FetchedDatum[];
  allCountryProductYear: FetchedCountryProductYearData[];
  allProductYear: FetchedProductYearData[];
  countryYear: {
    eci: number | null;
  };
  countryLookback: {
    largestContributingExportProduct: Array<{
      id: string;
    }>;
  };
}

interface Variables {
  location: string;
  year: number;
  yearRange: LookBackYearRange;
  productLevel: ProductLevel;
}

interface ExtraInputs {
  getFluentString: GetString;
  zIndex: number;
  rootElRef: RefObject<HTMLDivElement>;
  fluentBundle: FluentBundle;
  year: number;
  nodeSizing: ExportsChangeNodeSizing;
}

interface ContainerProps {
  variables: Variables;
  extraInputs: ExtraInputs;
}

const chartKey = "chart";
const spinnerKey = "spinner";

// Subtract away the sizes for various axis/legend elements:
const totalSizeToCanvasSize = (
  totalSize: MeasuredDOMElementSize
): MeasuredDOMElementSize => ({
  width:
    totalSize.width - marginalLegendSize - axisLabelsSize - yAxisTicksWidth,
  height:
    totalSize.height -
    topLabelSize -
    xAxisTicksHeight -
    axisLabelsSize -
    marginalLegendSize,
});

const Container = (props: ContainerProps) => {
  const elRef = props.extraInputs.rootElRef;
  const { size: totalSize } = useDOMElemSizeMonitoring({ elemRef: elRef });

  const {
    variables,
    extraInputs: { getFluentString, zIndex, fluentBundle, year, nodeSizing },
    extraInputs,
  } = props;
  const queryResult = useQuery<SuccessResponse, Variables>(QUERY, {
    variables,
  });
  const lastSuccessfulFetch = useLastSuccesfulFetch<
    SuccessResponse,
    Variables,
    ExtraInputs
  >({
    inputs: { queryResult: { ...queryResult, variables }, extraInputs },
    isFetchedDataEmpty: ({ countryProductLookback }: SuccessResponse) =>
      countryProductLookback.length === 0,
  });
  const codeTooltipLabel = getFluentString(
    "export-growth-dynamics-graph-hover-tooltip-code-label"
  );
  const countryTradeTooltipLabel = getFluentString(
    "export-growth-dynamics-graph-hover-tooltip-country-trade-label"
  );
  const worldTradeTooltipLabel = getFluentString(
    "export-growth-dynamics-graph-hover-tooltip-world-trade-label"
  );
  const growthPercentageLabel = getFluentString(
    "export-growth-dynamics-graph-hover-tooltip-growth-percentage-label"
  );

  const { getZIndex, promoteZIndex, restoreZIndex } = usePromotableZIndex({
    normalZIndices: zIndices,
    promotedZIndex,
  });

  let output: React.ReactElement<any> | null;
  if (queryResult.loading === true) {
    if (lastSuccessfulFetch !== undefined && totalSize !== undefined) {
      const {
        countryProductLookback,
        countryYear: { eci },
        countryLookback: { largestContributingExportProduct },
        allCountryProductYear,
      } = lastSuccessfulFetch.data;
      const { width, height } = totalSizeToCanvasSize(totalSize);
      const {
        nodes,
        maxGrowth,
        minGrowth,
        maxPCI,
        minPCI,
        tooltipMap,
      } = transform({
        fetchedData: countryProductLookback,
        allProductYearData: lastSuccessfulFetch.data.allProductYear,
        width,
        height,
        codeTooltipLabel,
        countryTradeTooltipLabel,
        worldTradeTooltipLabel,
        growthPercentageLabel,
        largestContributingExportProduct,
        nodeSizing,
        allCountryProductYearData: allCountryProductYear,
      });
      output = (
        <>
          <Chart
            nodes={nodes}
            maxComplexity={maxPCI}
            minComplexity={minPCI}
            maxGrowth={maxGrowth}
            minGrowth={minGrowth}
            eci={eci}
            getFluentString={getFluentString}
            yearRange={variables.yearRange}
            year={year}
            hoverTransitionDuration={hoverTransitionDuration}
            dataSetTransitionDuration={dataSetTransitionDuration}
            tooltipMap={tooltipMap}
            key={chartKey}
            fluentBundle={fluentBundle}
            getZIndex={getZIndex}
            promoteXAxisLabelContainerZIndex={() =>
              promoteZIndex("xAxisLabelContainer")
            }
            restoreZIndex={restoreZIndex}
          />
          <Loading key={spinnerKey} zIndex={getZIndex("spinner")} />
        </>
      );
    } else {
      output = (
        <>
          <Loading key={spinnerKey} zIndex={getZIndex("spinner")} />
        </>
      );
    }
  } else if (queryResult.error !== undefined) {
    console.error(queryResult.error);
    output = (
      <>
        <ErrorOverlay
          message={extraInputs.getFluentString(
            "general-data-fetch-failure-message"
          )}
        />
      </>
    );
  } else if (queryResult.data !== undefined) {
    if (totalSize !== undefined) {
      const { width, height } = totalSizeToCanvasSize(totalSize);
      const {
        countryProductLookback,
        countryYear: { eci },
        countryLookback: { largestContributingExportProduct },
        allCountryProductYear,
      } = queryResult.data;
      const {
        nodes,
        maxGrowth,
        minGrowth,
        maxPCI,
        minPCI,
        tooltipMap,
      } = transform({
        fetchedData: countryProductLookback,
        width,
        height,
        allProductYearData: queryResult.data.allProductYear,
        codeTooltipLabel,
        countryTradeTooltipLabel,
        worldTradeTooltipLabel,
        growthPercentageLabel,
        largestContributingExportProduct,
        nodeSizing,
        allCountryProductYearData: allCountryProductYear,
      });
      output = (
        <>
          <Chart
            nodes={nodes}
            maxComplexity={maxPCI}
            minComplexity={minPCI}
            maxGrowth={maxGrowth}
            minGrowth={minGrowth}
            eci={eci}
            getFluentString={getFluentString}
            yearRange={variables.yearRange}
            year={year}
            hoverTransitionDuration={hoverTransitionDuration}
            dataSetTransitionDuration={dataSetTransitionDuration}
            tooltipMap={tooltipMap}
            key={chartKey}
            fluentBundle={fluentBundle}
            getZIndex={getZIndex}
            promoteXAxisLabelContainerZIndex={() =>
              promoteZIndex("xAxisLabelContainer")
            }
            restoreZIndex={restoreZIndex}
          />
        </>
      );
    } else {
      output = (
        <>
          <Loading key={spinnerKey} zIndex={getZIndex("spinner")} />
        </>
      );
    }
  } else {
    output = null;
  }

  const style: React.CSSProperties = {
    zIndex,
  };

  return (
    <Root ref={elRef} style={style}>
      {output}
    </Root>
  );
};

type Props = Omit<ExtraInputs, "rootElRef"> &
  SlideElementProps & {
    year: number;
    location: string;
    yearRange: LookBackYearRange;
    fluentBundle: FluentBundle;
  };
const QueryWrapper = React.forwardRef(
  (props: Props, rootElRef: RefObject<HTMLDivElement>) => {
    const {
      year,
      yearRange,
      location,
      getFluentString,
      zIndex,
      fluentBundle,
      nodeSizing,
    } = props;
    const variables: Variables = {
      location,
      year,
      yearRange,
      productLevel: ProductLevel.twoDigit,
    };
    return (
      <Container
        extraInputs={{
          getFluentString,
          zIndex,
          fluentBundle,
          rootElRef,
          year,
          nodeSizing,
        }}
        variables={variables}
      />
    );
  }
);

export default QueryWrapper;
